import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import {
  BrandLinks,
  MarketingCampaignEntry,
} from '@principle-theorem/principle-core';
import {
  IMarketingCampaignEntry,
  IMarketingCampaignStep,
  IMarketingCampaignStepConversion,
  MarketingCampaignEntryStatus,
  MarketingCampaignStepConversionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  Firestore,
  HISTORY_DATE_TIME_FORMAT,
  isSameRef,
  multiSort,
  multiSwitchMap,
  sortByArrayIndex,
  Timestamp,
  WithRef,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BrandRouterLinkDirective } from '../../../navigation/brand-router-link.directive';
import { CampaignStepConversionStatusComponent } from '../campaign-step-conversion-status/campaign-step-conversion-status.component';
import { CampaignStepHeaderComponent } from '../campaign-step-header/campaign-step-header.component';
import { CampaignStepIconComponent } from '../campaign-step-icon/campaign-step-icon.component';
import { CampaignStepNumberComponent } from '../campaign-step-number/campaign-step-number.component';

export interface ICampaignEntryStepPair {
  stepConversion: WithRef<IMarketingCampaignStepConversion>;
  stepConfig: WithRef<IMarketingCampaignStep>;
}

function getStepConversionTooltip(
  stepConversion: IMarketingCampaignStepConversion
): string | undefined {
  switch (stepConversion.status) {
    case MarketingCampaignStepConversionStatus.Cancelled:
      return stepConversion.cancelledReason;
    case MarketingCampaignStepConversionStatus.Skipped:
      return stepConversion.skippedReason;
    case MarketingCampaignStepConversionStatus.Failed:
      return stepConversion.failedReason;
    default:
      return undefined;
  }
}

@Component({
  selector: 'pr-campaign-entry-progress',
  templateUrl: './campaign-entry-progress.component.html',
  styleUrl: './campaign-entry-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    NgCommonModule,
    MatIcon,
    BrandRouterLinkDirective,
    CampaignStepNumberComponent,
    CampaignStepConversionStatusComponent,
    CampaignStepHeaderComponent,
    CampaignStepIconComponent,
  ],
})
export class CampaignEntryProgressComponent {
  readonly dateFormat = HISTORY_DATE_TIME_FORMAT;
  campaignEntry = input.required<WithRef<IMarketingCampaignEntry>>();
  steps: Signal<ICampaignEntryStepPair[]>;
  brandLinks = BrandLinks;
  steps$: Observable<ICampaignEntryStepPair[]>;
  completedStatus = MarketingCampaignStepConversionStatus.Completed;
  pendingStatus = MarketingCampaignStepConversionStatus.Pending;
  getStepConversionTooltip = getStepConversionTooltip;
  enrolledByRunRef: Signal<IMarketingCampaignEntry['enrolledByCampaignRunRef']>;
  isPreview: Signal<boolean>;

  constructor() {
    this.steps$ = toObservable(this.campaignEntry).pipe(
      switchMap((entry) => this._getStepPairs$(entry))
    );
    this.steps = toSignal(this.steps$, { initialValue: [] });

    this.enrolledByRunRef = computed(
      () => this.campaignEntry().enrolledByCampaignRunRef
    );
    this.isPreview = computed(
      () => this.campaignEntry().status === MarketingCampaignEntryStatus.Preview
    );
  }

  showActiveStep(step: WithRef<IMarketingCampaignStepConversion>): boolean {
    return this.isCurrentStep(step) && !this.isStepDone(step);
  }

  showStepDate(step: WithRef<IMarketingCampaignStepConversion>): boolean {
    return this.isCurrentStep(step) || this.isStepDone(step);
  }

  isCurrentStep(step: WithRef<IMarketingCampaignStepConversion>): boolean {
    return isSameRef(this.campaignEntry().currentStep, step.ref);
  }

  isStepDone(step: WithRef<IMarketingCampaignStepConversion>): boolean {
    const doneStatuses = [
      MarketingCampaignStepConversionStatus.Disabled,
      MarketingCampaignStepConversionStatus.Skipped,
      MarketingCampaignStepConversionStatus.Completed,
      MarketingCampaignStepConversionStatus.Converted,
      MarketingCampaignStepConversionStatus.Failed,
      MarketingCampaignStepConversionStatus.Cancelled,
      MarketingCampaignStepConversionStatus.Unsubscribed,
      MarketingCampaignStepConversionStatus.Unenrolled,
      MarketingCampaignStepConversionStatus.ExceededCommunicationLimit,
    ];
    return doneStatuses.includes(step.status);
  }

  async getStepDate(
    stepConversion: WithRef<IMarketingCampaignStepConversion>
  ): Promise<Timestamp | undefined> {
    if (!stepConversion.automationRef) {
      return;
    }

    const automation = await Firestore.getDoc(stepConversion.automationRef);
    return automation.triggerDate;
  }

  private _getStepPairs$(
    campaignEntry: WithRef<IMarketingCampaignEntry>
  ): Observable<ICampaignEntryStepPair[]> {
    const stepOrder = campaignEntry.steps.map((step) => step.path);
    return all$(MarketingCampaignEntry.stepConversionCol(campaignEntry)).pipe(
      multiSwitchMap((stepConversion) =>
        Firestore.doc$(stepConversion.stepRef).pipe(
          map((stepConfig) => ({ stepConversion, stepConfig }))
        )
      ),
      multiSort(sortByArrayIndex(stepOrder, (item) => item.stepConfig.ref.path))
    );
  }
}
